import { type DepartmentMailbox, normalizeMailboxes } from '@/pages/pageCommunicationSettings/departmentMailboxModel'
import { type MailboxTopicSettings, normalizeMailboxTopics } from '@/pages/pageCommunicationSettings/mailboxTopicsModel'
import { getRequestGeneric, putRequestGeneric } from '@/services/backendApiGeneric'

/** Ebene, auf der ein initialer Empfänger hinterlegt ist. */
export type ScopeType = 'organization' | 'location' | 'department'

export interface RecipientScope {
  type: ScopeType
  id: number
}

export interface UserScopes {
  idUser: number
  scopes: RecipientScope[]
}

/**
 * Lädt die initialen Benachrichtigungs-Empfänger der eigenen Organisation.
 * Ein Aufruf deckt die gesamte Mitarbeitendentabelle ab — es gibt bewusst
 * keinen Request pro Zeile.
 */
export async function loadNotificationRecipients (): Promise<UserScopes[]> {
  const data = await getRequestGeneric('/protected/notification-recipients')
  return data?.recipients ?? []
}

/**
 * Ersetzt die Geltungsbereiche einer Person vollständig. Ein leeres Array
 * entfernt sie aus der Empfängerliste.
 */
export async function saveNotificationRecipient (idUser: number, scopes: RecipientScope[]): Promise<void> {
  await putRequestGeneric(`/protected/notification-recipients/${idUser}`, { scopes }, true)
}

// ── Standard-Empfänger ohne Sachbearbeiter (Kundenfeedback 02.10.26, VI.) ──

export interface FallbackRecipient {
  idUser: number
  email: string
  firstName: string
  lastName: string
}

export interface FallbackPerson {
  idUser: number
  firstName: string
  lastName: string
  email: string
  userRole: string
  /** Abteilungen der Person (Warnhinweis im Dialog zur Glocke). */
  departmentIds?: number[]
}

export interface FallbackSettings {
  /** Zusätzliches Sammel-Postfach (leer = keines). */
  fallbackEmail: string
  /** true = nur die hinterlegten Empfänger, nicht automatisch die Zentrale. */
  replacesCentral: boolean
  /** Aktuelle Standard-Empfänger (Organisationsebene). */
  recipients: FallbackRecipient[]
  /** Wählbare Personen der Organisation. */
  people: FallbackPerson[]
  /** Wirksames Postfach je Abteilung (Abteilung → Standort → Unternehmen); nur Anzeige. */
  mailboxes?: DepartmentMailbox[]
  /** Darf die Sitzung speichern? Nur Anzeige – das Backend prüft erneut. */
  canEdit: boolean
}

export interface FallbackSettingsPayload {
  fallbackEmail: string
  replacesCentral: boolean
  recipientUserIds: number[]
}

/** Lädt Sammel-Postfach, Zentrale-Schalter und Standard-Empfänger der Organisation. */
export async function loadFallbackSettings (): Promise<FallbackSettings> {
  const data = await getRequestGeneric('/protected/notification-recipients/settings')
  return {
    fallbackEmail: data?.fallbackEmail ?? '',
    replacesCentral: !!data?.replacesCentral,
    recipients: Array.isArray(data?.recipients) ? data.recipients : [],
    people: Array.isArray(data?.people) ? data.people : [],
    mailboxes: normalizeMailboxes(data?.mailboxes),
    canEdit: !!data?.canEdit,
  }
}

/** Speichert alle drei Einstellungen in einem Aufruf (ersetzt den Bestand). */
export async function saveFallbackSettings (payload: FallbackSettingsPayload): Promise<void> {
  await putRequestGeneric('/protected/notification-recipients/settings', payload, true)
}

// ── Postfach-Adressen aller Ebenen (Kundenfeedback KS 29.10.2026, I) ──

export type MailboxLevel = 'company' | 'location' | 'department'

/** Eigene Adresse einer Ebene, nie die geerbte. */
export interface MailboxAddress {
  level: MailboxLevel
  id: number
  /** Unternehmen des Standorts bzw. Standort der Abteilung; 0 beim Unternehmen. */
  idParent: number
  name: string
  email: string
}

export interface MailboxChange {
  level: MailboxLevel
  id: number
  email: string
}

export interface MailboxAddresses {
  addresses: MailboxAddress[]
  /** Darf die Sitzung speichern? Nur Anzeige – das Backend prüft erneut. */
  canEdit: boolean
}

/** Lädt Unternehmen, Standorte und Abteilungen samt eigener Adresse. */
export async function loadMailboxAddresses (): Promise<MailboxAddresses> {
  const data = await getRequestGeneric('/protected/notification-recipients/mailbox-addresses')
  return {
    addresses: Array.isArray(data?.addresses) ? data.addresses : [],
    canEdit: !!data?.canEdit,
  }
}

/** Speichert geänderte Adressen; alle oder keine. */
export async function saveMailboxAddresses (changes: MailboxChange[]): Promise<void> {
  await putRequestGeneric('/protected/notification-recipients/mailbox-addresses', { changes }, true)
}

// ── Themen für Postfächer (Kundenfeedback 14.10.2026) ──

/** Lädt, welche Digest-Themen die Postfächer der Organisation erhalten. */
export async function loadMailboxTopics (): Promise<MailboxTopicSettings> {
  return normalizeMailboxTopics(await getRequestGeneric('/protected/notification-recipients/mailbox-topics'))
}

/** Speichert die Auswahl für einzelne Themen; nicht genannte bleiben unverändert. */
export async function saveMailboxTopics (entries: Array<{ topic: string, enabled: boolean }>): Promise<void> {
  await putRequestGeneric('/protected/notification-recipients/mailbox-topics', entries, true)
}
